import { useState, useEffect } from 'react';
import { adminApi } from '../../../utils/api';
import { Spinner, Card, Badge, Button } from '../shared';

const SHARE_BASE_URL = window.location.origin;

const fmtBytes = (n) => {
    if (n == null) return '-';
    if (n >= 1_048_576) return `${(n / 1_048_576).toFixed(1)} MB`;
    if (n >= 1_024) return `${(n / 1_024).toFixed(1)} KB`;
    return `${n} B`;
};

const fmtDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: '2-digit',
        year: '2-digit',
    });
};

export default function UserContentTab({ userId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [successMsg, setSuccessMsg] = useState(null);

    useEffect(() => {
        loadData();
    }, [userId]);

    const loadData = async () => {
        setLoading(true);
        try {
            const result = await adminApi.getUserContent(userId, { limit: 50, offset: 0 });
            setData(result);
        } catch (err) {
            console.error('Failed to load content:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenFile = async (fileId) => {
        try {
            const res = await adminApi.getContentUrlDownload(fileId);
            window.open(res.download_url, '_blank');
        } catch (err) {
            console.error('Failed to get download URL:', err);
        }
    };

    const handleOpenShare = (shareId) => {
        window.open(`${SHARE_BASE_URL}/share/${shareId}`, '_blank');
    };

    const handleCopyShareLink = async (shareId) => {
        try {
            await navigator.clipboard.writeText(`${SHARE_BASE_URL}/share/${shareId}`);
            setSuccessMsg('Link copied');
            setTimeout(() => setSuccessMsg(null), 2000);
        } catch {
            handleOpenShare(shareId);
        }
    };

    const handleToggleShare = async (shareId, currentActive) => {
        try {
            await adminApi.updateShareStatus(shareId, !currentActive);
            setData(prev => ({
                ...prev,
                shares: prev.shares.map(s => s.share_id === shareId ? { ...s, is_active: !currentActive } : s),
            }));
        } catch (err) {
            console.error('Failed to toggle share:', err);
        }
    };

    const handleDeleteShare = async (shareId) => {
        if (!confirm(`Delete share "${shareId}"?`)) return;
        try {
            await adminApi.deleteShare(shareId);
            setData(prev => ({
                ...prev,
                shares: prev.shares.filter(s => s.share_id !== shareId),
                total_shares: Math.max(0, (prev.total_shares || 0) - 1),
            }));
        } catch (err) {
            console.error('Failed to delete share:', err);
        }
    };

    if (loading) {
        return <Spinner text="Loading content..." center />;
    }

    if (!data) {
        return <p className="text-sm text-gray-400 text-center py-12">No data available</p>;
    }

    return (
        <div className="space-y-6 h-full overflow-y-auto">
            {successMsg && (
                <div className="fixed top-4 right-4 z-50 px-3 py-1.5 bg-gray-900 text-white text-xs rounded-lg shadow-lg animate-fade-in">
                    {successMsg}
                </div>
            )}

            <Card title={`Uploads (${data.total_uploads || 0})`}>
                {data.uploads && data.uploads.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-200">
                                    <th className="text-left py-2 text-xs font-medium text-gray-500">Filename</th>
                                    <th className="text-left py-2 text-xs font-medium text-gray-500">Type</th>
                                    <th className="text-right py-2 text-xs font-medium text-gray-500">Size</th>
                                    <th className="text-right py-2 text-xs font-medium text-gray-500">Uploaded</th>
                                    <th className="text-right py-2 text-xs font-medium text-gray-500">Access</th>
                                    <th className="text-right py-2 text-xs font-medium text-gray-500 w-16">Action</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {data.uploads.map((u) => (
                                    <tr key={u.file_id} className="hover:bg-gray-50">
                                        <td className="py-2">
                                            <button
                                                onClick={() => handleOpenFile(u.file_id)}
                                                className="truncate max-w-xs font-mono text-xs text-blue-600 hover:text-blue-800 hover:underline text-left"
                                            >
                                                {u.original_filename}
                                            </button>
                                        </td>
                                        <td className="py-2 text-xs text-gray-500">{u.content_type}</td>
                                        <td className="py-2 text-right text-xs text-gray-700">{fmtBytes(u.file_size)}</td>
                                        <td className="py-2 text-right text-xs text-gray-500">{fmtDate(u.created_at)}</td>
                                        <td className="py-2 text-right text-xs text-gray-700">{u.access_count || 0}</td>
                                        <td className="py-2 text-right">
                                            <Button variant="ghost" size="xs" onClick={() => handleOpenFile(u.file_id)}>Open</Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className="text-sm text-gray-400 text-center py-4">No uploads</p>
                )}
            </Card>

            <Card title={`Shares (${data.total_shares || 0})`}>
                {data.shares && data.shares.length > 0 ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-200">
                                    <th className="text-left py-2 text-xs font-medium text-gray-500">Title</th>
                                    <th className="text-left py-2 text-xs font-medium text-gray-500 w-16">Status</th>
                                    <th className="text-right py-2 text-xs font-medium text-gray-500 w-14">Views</th>
                                    <th className="text-right py-2 text-xs font-medium text-gray-500 w-20">Created</th>
                                    <th className="text-right py-2 text-xs font-medium text-gray-500 w-40">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {data.shares.map((s) => (
                                    <tr key={s.share_id} className="hover:bg-gray-50">
                                        <td className="py-2">
                                            <button
                                                onClick={() => handleOpenShare(s.share_id)}
                                                className="truncate max-w-xs text-xs text-blue-600 hover:text-blue-800 hover:underline text-left block"
                                            >
                                                {s.title || s.share_id}
                                            </button>
                                        </td>
                                        <td className="py-2">
                                            <Badge variant={s.is_active ? 'success' : 'default'}>
                                                {s.is_active ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </td>
                                        <td className="py-2 text-right text-xs text-gray-700">{s.view_count || 0}</td>
                                        <td className="py-2 text-right text-xs text-gray-500">{fmtDate(s.created_at)}</td>
                                        <td className="py-2 text-right">
                                            <div className="flex items-center justify-end gap-1">
                                                <button
                                                    className="p-1 rounded hover:bg-gray-100 text-gray-400 hover:text-blue-600 transition-colors"
                                                    onClick={() => handleCopyShareLink(s.share_id)}
                                                    title="Copy link"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3.5 h-3.5">
                                                        <path d="M12.232 4.232a2.5 2.5 0 013.536 3.536l-1.225 1.224a.75.75 0 001.061 1.06l1.224-1.224a4 4 0 00-5.656-5.656l-3 3a4 4 0 00.225 5.865.75.75 0 00.977-1.138 2.5 2.5 0 01-.142-3.667l3-3z" />
                                                        <path d="M11.603 7.963a.75.75 0 00-.977 1.138 2.5 2.5 0 01.142 3.667l-3 3a2.5 2.5 0 01-3.536-3.536l1.225-1.224a.75.75 0 00-1.061-1.06l-1.224 1.224a4 4 0 105.656 5.656l3-3a4 4 0 00-.225-5.865z" />
                                                    </svg>
                                                </button>
                                                <Button variant="ghost" size="xs" onClick={() => handleOpenShare(s.share_id)}>Open</Button>
                                                <Button variant="ghost" size="xs" onClick={() => handleToggleShare(s.share_id, s.is_active)}>
                                                    {s.is_active ? 'Deactivate' : 'Activate'}
                                                </Button>
                                                <Button
                                                    variant="ghost" size="xs"
                                                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                    onClick={() => handleDeleteShare(s.share_id)}
                                                >
                                                    Delete
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p className="text-sm text-gray-400 text-center py-4">No shares</p>
                )}
            </Card>
        </div>
    );
}
